import React, { useEffect } from 'react'
import styled from '@emotion/styled'
import PropTypes from 'prop-types'
import { useFormContext } from 'react-hook-form'
import ReactQuill from 'react-quill-new'
import 'react-quill-new/dist/quill.snow.css'

import { theme } from '../../themes/theme'
import { FlexColumn } from '../styles/Containers'
import { ErrorText } from '../styles/Text'
import Label from './Label'

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link']
  ]
}

const quillFormats = ['header', 'bold', 'italic', 'underline', 'strike', 'color', 'list', 'link']

const EditorWrapper = styled(FlexColumn)`
  min-width: 0;
  max-width: 100%;
`

const EditorContainer = styled(FlexColumn)`
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  border: 1px solid ${theme.colors.inputField.border};
  border-radius: ${theme.borderRadius.input};
  background: ${theme.colors.inputField.background};
  opacity: ${props => (props.disabled ? '0.6' : '1')};

  .quill {
    min-width: 0;
    max-width: 100%;
  }

  .ql-toolbar.ql-snow {
    border: none;
    border-bottom: 1px solid ${theme.colors.inputField.border};
    border-top-left-radius: ${theme.borderRadius.input};
    border-top-right-radius: ${theme.borderRadius.input};
    background: ${theme.colors.background.white};
    white-space: normal;
  }

  .ql-container.ql-snow {
    border: none;
    font-family: ${theme.fonts.body};
    font-size: 14px;
    line-height: 18px;
  }

  .ql-editor {
    min-height: 160px;
    padding: 8px 15px;
    overflow-wrap: anywhere;
  }

  .ql-snow .ql-tooltip input[data-link]::placeholder {
    color: transparent;
  }
`

const LabelWrapper = styled.div`
  opacity: ${props => (props.disabled ? '0.4' : '1')};
`

function RichTextEditor({ disabled = false, id = 'body', label = '' }) {
  const { getValues, setValue, watch, formState } = useFormContext()
  const value = watch(id) || ''

  useEffect(() => {
    const currentValue = getValues(id)
    if (currentValue === undefined || currentValue === null) {
      setValue(id, '')
    }
  }, [getValues, setValue])

  const error = !!formState.errors[id]
  const helperText = formState.errors?.[id]?.message

  return (
    <EditorWrapper gap="5px">
      <LabelWrapper disabled={disabled}>
        <Label label={label} />
      </LabelWrapper>
      <EditorContainer data-testid="editor-container" disabled={disabled}>
        <ReactQuill
          theme="snow"
          value={value}
          modules={quillModules}
          formats={quillFormats}
          readOnly={disabled}
          onChange={html => setValue(id, html, { shouldDirty: true, shouldTouch: true })}
        />
      </EditorContainer>
      {error && <ErrorText role="alert">{helperText}</ErrorText>}
    </EditorWrapper>
  )
}

RichTextEditor.propTypes = {
  disabled: PropTypes.bool,
  id: PropTypes.string,
  label: PropTypes.string
}

export default RichTextEditor
